/* eslint-disable no-param-reassign */
/* eslint-disable no-new */
const d3 = require('d3')

const { isGuffable, guff } = require('bam-node-baseball-savant/utils/guff')
const {
    normalizeAccents,
} = require('bam-node-baseball-savant/utils/normalize-accents')

function idGenerator(data, prefix = 'text') {
    // Take the opinion that it doesn't matter how many _undefined we have, as long as it's unique in the end
    return `${prefix}_${data.player_id}_${data.game_date}_${data.inning}_${data.outs_when_up}_${data.year}`
}

function getColorVal(type, row) {
    switch (type) {
        case 'pitch_velocity':
            return row.velocity || row.release_speed
        case 'spin_rate':
            return row.spin_rate || row.release_spin_rate
        default:
            return row[type]
    }
}

const MakeChart = function MakeChart(initOpts, initCallback) {
    function ScatterChart(opts, callback) {
        this.opts = opts

        let percentDecrease = 1
        /// 870 MAX WIDTH

        if (!this.opts.width || this.opts.width > 870) {
            this.margin = { top: 30, right: 30, bottom: 80, left: 70 }
            this.width = 870 - this.margin.left - this.margin.right
            this.height = 500 - this.margin.top - this.margin.bottom
        } else {
            percentDecrease = 870 / this.opts.width
            this.margin = {
                top: 30 / percentDecrease,
                right: 30 / percentDecrease,
                bottom: 80 / percentDecrease,
                left: 70 / percentDecrease,
            }
            this.width =
                870 / percentDecrease - this.margin.left - this.margin.right
            this.height =
                500 / percentDecrease - this.margin.top - this.margin.bottom
        }

        this.scaleSize = function scaleSize(size) {
            return size / percentDecrease
        }

        this.xscale = d3.scaleLinear().range([0, this.width])

        this.yscale = d3.scaleLinear().range([this.height, 0])

        this.radius = d3
            .scaleLinear()
            .range([this.scaleSize(7), this.scaleSize(7)])

        this.xAxis = d3
            .axisBottom()
            .tickSize(-this.height)
            .scale(this.xscale)
            .tickPadding(this.scaleSize(10))
            .tickFormat(getSuffix(this.opts.xVal))

        this.yAxis = d3
            .axisLeft()
            .tickSize(-this.width)
            .scale(this.yscale)
            .tickPadding(this.scaleSize(10))
            .tickFormat(getSuffix(this.opts.yVal))

        this.colorPitch = d3
            .scaleLinear()
            .domain([65, 86, 100])
            .range(['#3661AD', '#c2c2c2', '#D82129'])
            .clamp(true)

        this.colorExit = d3
            .scaleLinear()
            .domain([80, 90, 100])
            .range(['#3661AD', '#c2c2c2', '#D82129'])
            .clamp(true)

        this.colorSpin = d3
            .scaleLinear()
            .domain([1000, 2000, 3000])
            .range(['#3661AD', '#c2c2c2', '#D82129'])
            .clamp(true)

        this.colorBA = d3
            .scaleLinear()
            .domain([0.2, 0.25, 0.3])
            .range(['#3661AD', '#c2c2c2', '#D82129'])
            .clamp(true)

        this.colorSLG = d3
            .scaleLinear()
            .domain([0.3, 0.4, 0.5])
            .range(['#3661AD', '#c2c2c2', '#D82129'])
            .clamp(true)

        this.colorISO = d3
            .scaleLinear()
            .domain([0.1, 0.18, 0.26])
            .range(['#3661AD', '#c2c2c2', '#D82129'])
            .clamp(true)

        d3.select(`#svg_${this.opts.id}`).remove()

        this.svg = d3
            .select(`#${this.opts.id}`)
            .append('svg')
            .attr('width', this.width + this.margin.left + this.margin.right)
            .attr('height', this.height + this.margin.top + this.margin.bottom)
            .attr('id', `svg_${this.opts.id}`)

        this.init()

        if (callback) {
            if (this.scatterChart) {
                callback(this.scatterChart.html())
            } else {
                callback()
            }
        }
    }

    ScatterChart.prototype.init = function init() {
        let { data } = this.opts

        data = data.map((d) => ({
            ...d,
            api_break_z_induced: Number(d.api_break_z_induced) * 12,
            api_break_x: Number(d.api_break_x) * 12,
            api_break_x_arm: Number(d.api_break_x_arm) * 12,
            api_break_x_batter_in: Number(d.api_break_x_batter_in) * 12,
            api_break_z_with_gravity: Number(d.api_break_z_with_gravity) * 12,
        }))

        // Add white background

        if (!this.isServerSide) {
            this.toolTipEl = d3
                .select(window.document.body)
                .append('div')
                .attr('class', 'tooltip')
                .style('id', `tooltip_${this.opts.id}`)
                .style('opacity', 0)
                .style('posiition', 'absolute')
                .style('position', 'absolute')
                .style('padding', '6px')
                .style('font', '12px sans-serif')
                .style('background', '#ffffff')
                .style('line-height', 1.8)
                .style('border', 'solid 1px #000')
                .style('border-radius', '8px')
                .style('pointer-events', 'none')
        }

        // Filter out x and y values that are null
        data = data.filter((d) => {
            const x = d[this.opts.xVal]
            const y = d[this.opts.yVal]
            const color = getColorVal(this.opts.colorType, d)
            return (
                x !== null &&
                y !== null &&
                (this.opts.colorType === 'none' || color !== null)
            )
        })

        data.forEach((d) => {
            d.y = null
            d.x = null
            d.r = 1

            if (this.opts.yVal === 'swing_and_miss') {
                if (d.swings !== 'null' && d.whiffs !== 'null') {
                    d.y = (d.whiffs / d.swings) * 100
                }
            } else {
                d.y = Number(d[this.opts.yVal])
            }

            if (this.opts.xVal === 'swing_and_miss') {
                if (d.swings !== 'null' && d.whiffs !== 'null') {
                    d.x = (d.whiffs / d.swings) * 100
                }
            } else {
                d.x = Number(d[this.opts.xVal])
            }

            if (d.pitch && d.pitches !== 'null') {
                d.r = Number(d.pitches)
            }
        })

        const y_domain = getDomain(this.opts.yVal, 'y', data)
        const x_domain = getDomain(this.opts.xVal, 'x', data)
        data.sort((a, b) => b.r - a.r)

        this.yscale.domain([y_domain.min, y_domain.max]).nice()

        this.xscale.domain([x_domain.min, x_domain.max]).nice()

        this.radius.domain([0, d3.max(data, (d) => d.r)]).nice()

        this.svg
            .append('rect')
            .attr('height', '100%')
            .attr('width', '100%')
            .attr('fill', '#e8e8e8')

        this.svg = this.svg
            .append('g')
            .attr(
                'transform',
                `translate(${this.margin.left},${this.margin.top})`
            )

        this.svg
            .append('g')
            .attr('class', 'x axis')
            .attr('transform', `translate(0,${this.height})`)
            .call(this.xAxis)

        this.svg
            .append('g')
            .attr('class', 'y axis')
            .attr('transform', 'translate(0,0)')
            .call(this.yAxis)

        this.svg
            .selectAll('.axis line, .axis path')
            .attr('fill', 'none')
            .attr('stroke', '#c2c2c2')

        this.svg
            .selectAll('.x text')
            .style('text-anchor', 'end')
            .style('font-family', 'Roboto Condensed')
            .style('font-size', this.scaleSize(11))
            .attr('dx', '-.8em')
            .attr('dy', '.15em')
            .attr('transform', 'rotate(-65)')

        this.svg
            .selectAll('.y text')
            .style('font-family', 'Roboto Condensed')
            .style('font-size', this.scaleSize(11))

        const group = this.svg
            .selectAll('g.bubble')
            .data(data)
            .enter()
            .append('g')
            .attr(
                'transform',
                (d) => `translate(${this.xscale(d.x)},${this.yscale(d.y)})`
            )

        group
            .append('circle')
            .attr('r', (d) => Math.abs(this.radius(d.r)))
            .attr('id', (d) => `id_${d.player_id}`)
            .style('fill', (d, i, nodes) => {
                if (
                    this.opts.filter.length >= 3 &&
                    d.player_name &&
                    normalizeAccents(d.player_name).includes(
                        normalizeAccents(this.opts.filter)
                    )
                ) {
                    makeTooltip(d, nodes[i], this)
                    return '#f00'
                }

                if (this.opts.colorType === 'none') {
                    return '#399098'
                }

                const hiddenColor = 'rgba(0, 0, 0, 0)'
                const value = getColorVal(this.opts.colorType, d)

                if (value === null) {
                    // This should already have been filtered, but just in case
                    return hiddenColor
                }

                if (this.opts.colorType === 'pitch_velocity') {
                    return this.colorPitch(value)
                }
                if (this.opts.colorType === 'launch_speed') {
                    return this.colorExit(value)
                }
                if (this.opts.colorType === 'spin_rate') {
                    return this.colorSpin(value)
                }
                if (this.opts.colorType === 'ba') {
                    return this.colorBA(value)
                }
                if (this.opts.colorType === 'slg') {
                    return this.colorSLG(value)
                }
                if (this.opts.colorType === 'iso') {
                    return this.colorISO(value)
                }

                return 'blue'
            })
            .attr('opacity', (d) => {
                if (
                    this.opts.filter.length >= 3 &&
                    d.player_name
                        .toUpperCase()
                        .indexOf(this.opts.filter.toUpperCase()) === -1
                ) {
                    return 0.15
                }
                if (this.opts.opacity) {
                    return this.opts.opacity
                }
                return 1
            })
            .on('mouseover', (d) => {
                this.toolTipEl.style('opacity', 0.9)

                let gameDate = ''
                let year = ''
                if (d.game_date) {
                    gameDate += ` - ${d.game_date}`
                } else if (d.year) {
                    year += ` - ${d.year}`
                }

                this.toolTipEl
                    .html(d.player_name + gameDate + year)
                    .style('left', `${d3.event.pageX + 10}px`)
                    .style('top', `${d3.event.pageY - 10}px`)
            })
            .on('mouseout', () => {
                this.toolTipEl.style('opacity', 0)
            })
            .on('click', (d, i, nodes) => toggleToolTip(d, nodes[i], this))

        this.svg
            .append('text')
            .attr('x', 6)
            .attr('y', -2)
            .style('font-family', 'Roboto Condensed')
            .style('font-size', this.scaleSize(14))
            .text(this.opts.yTitle)

        this.svg
            .append('text')
            .attr('x', this.width - 2)
            .attr('y', this.height - 6)
            .attr('text-anchor', 'end')
            .style('font-family', 'Roboto Condensed')
            .style('font-size', this.scaleSize(14))
            .text(this.opts.xTitle)

        // LINEAR REGRESSION
        if (this.opts.showRegression) {
            const lg = this.parseData(data)

            const line = d3
                .line()
                .x((d) => this.xscale(d.x))
                .y((d) => this.yscale(d.yhat))

            this.svg
                .append('path')
                .datum(lg)
                .attr('class', 'line')
                .attr('stroke', '#C90813')
                .attr('d', line)

            this.svg
                .append('text')
                .attr('x', this.width / 2)
                .attr('y', -6)
                .style('font-family', 'Roboto Condensed')
                .text(`r2: ${lg[0].r2.toFixed(2)}`)
        }

        if (this.opts.colorType !== 'none') {
            this.svg
                .append('text')
                .attr('x', this.width - 62)
                .attr('y', -2)
                .attr('text-anchor', 'end')
                .style('font-family', 'Roboto Condensed')
                .style('font-size', this.scaleSize(14))
                .text(this.opts.colorTitle)

            let legend = []
            if (this.opts.colorType === 'ba') {
                legend = [0.2, 0.225, 0.25, 0.275, 0.3]
            }
            if (this.opts.colorType === 'slg') {
                legend = [0.3, 0.35, 0.4, 0.45, 0.5]
            }
            if (this.opts.colorType === 'iso') {
                legend = [0.1, 0.14, 0.18, 0.22, 0.26]
            }
            if (this.opts.colorType === 'launch_speed') {
                legend = [80, 85, 90, 95, 100]
            }
            if (this.opts.colorType === 'pitch_velocity') {
                legend = [65, 72, 79, 86, 93, 100]
            }
            if (this.opts.colorType === 'spin_rate') {
                legend = [1000, 1500, 2000, 2500, 3000]
            }

            this.svg
                .selectAll('colorLegend')
                .data(legend)
                .enter()
                .append('circle')
                .attr('cx', (d, i) => {
                    const sub = i * 10
                    return this.width - (50 - sub)
                })
                .attr('cy', -8)
                .attr('r', this.scaleSize(7))
                .attr('fill', (d) => {
                    if (this.opts.colorType === 'ba') {
                        return this.colorBA(d)
                    }
                    if (this.opts.colorType === 'slg') {
                        return this.colorSLG(d)
                    }
                    if (this.opts.colorType === 'iso') {
                        return this.colorISO(d)
                    }
                    if (this.opts.colorType === 'launch_speed') {
                        return this.colorExit(d)
                    }
                    if (this.opts.colorType === 'pitch_velocity') {
                        return this.colorPitch(d)
                    }
                    if (this.opts.colorType === 'spin_rate') {
                        return this.colorSpin(d)
                    }

                    return 'transparent'
                })
        }
    }

    function toggleToolTip(d, node, chart) {
        const id = idGenerator(d)
        const idWithHash = `#${id}`

        if (!d3.select(idWithHash).empty()) {
            d3.select(idWithHash).remove()
            d3.select(node).style('stroke', 'none')
        } else if (!d3.select(`#text_${d.player_id}`).empty()) {
            d3.select(`#text_${d.player_id}`).remove()
            d3.select(node).style('stroke', 'none')
        } else {
            d3.select(node).style('stroke', '#dda398').style('stroke-width', 3)
            makeTooltip(d, node, chart)
        }
    }

    function makeTooltip(d, node, chart) {
        const id = idGenerator(d)

        const parentNode = d3.select(node.parentNode).node().transform
            .baseVal[0].matrix
        const x = parentNode.e
        const y = parentNode.f

        let text = d.player_name
        text += d.year ? ` - ${d.year}` : ''

        const leftSide = x + 100 > chart.width
        const nudge = leftSide ? -10 : 10
        const nudgeY = 1

        chart.svg
            .append('text')
            .attr('transform', () => `translate(${x + nudge},${y + nudgeY})`)
            .attr('id', id)
            .attr('dominant-baseline', 'middle')
            .attr('text-anchor', leftSide ? 'end' : 'start')
            .style('font-family', 'Roboto Condensed')
            .style('font-weight', 'bold')
            .style('fill', '#FF2700')
            .style('font-size', chart.scaleSize(13))
            .text(text)
    }

    // Let's return a function or undefined
    function getSuffix(type) {
        if (
            type === 'api_break_x' ||
            type === 'api_break_x_arm' ||
            type === 'api_break_x_batter_in' ||
            type === 'api_break_z_with_gravity' ||
            type === 'api_break_z_induced'
        ) {
            return (d) => `${Math.round(Number(d).toFixed(1))} in.`
        }

        if (isGuffable(type)) {
            return (d) => guff(Number(d), type)
        }

        if (
            type === 'ba' ||
            type === 'slg' ||
            type === 'iso' ||
            type === 'xba' ||
            type === 'woba' ||
            type === 'xwoba' ||
            type === 'xiso' ||
            type === 'xobp' ||
            type === 'obp' ||
            type === 'xbacon' ||
            type === 'babip' ||
            type === 'wobacon' ||
            type === 'xwobacon' ||
            type === 'batting_avg' ||
            type === 'slg_percent' ||
            type === 'on_base_percent' ||
            type === 'on_base_plus_slg' ||
            type === 'isolated_power' ||
            type === 'bacon' ||
            type === 'xbacon' ||
            type === 'xslg' ||
            type === 'xslgdiff' ||
            type === 'xbadiff' ||
            type === 'wobadiff'
        ) {
            return (d) => Number(d).toFixed(3).replace('0.', '.')
        }

        if (type === 'eff_min_vel') {
            return (d) => `${Number(d).toFixed(1)} MPH`
        }
        if (
            type === 'velocity' ||
            type === 'launch_speed' ||
            type === 'exit_velocity_avg' ||
            type === 'maxeff_arm_2b_3b_sba'
        ) {
            return (d) => `${Number(d).toFixed(0)} MPH`
        }
        if (
            type === 'pitch_percent' ||
            type === 'swing_and_miss' ||
            type.indexOf('_percent') > -1
        ) {
            return (d) => `${Number(d).toFixed(0)}%`
        }
        if (type === 'rounded_sprint_speed') {
            return (d) => `${Number(d).toFixed(1)}ft/s`
        }
        if (type === 'release_extension') {
            return (d) => `${Number(d).toFixed(1)}ft.`
        }
        // might be unused, check before removal
        if (type === 'pfx_x' || type === 'pfx_z') {
            return (d) => `${Number(d).toFixed(1)}	″`
        }
        if (
            type === 'x0' ||
            type === 'z0' ||
            type === 'release_pos_x' ||
            type === 'release_pos_z' ||
            type === 'px' ||
            type === 'pz' ||
            type === 'plate_x' ||
            type === 'plate_z'
        ) {
            return (d) => `${Number(d).toFixed(1)}	ft`
        }
        if (type.indexOf('start_distance') > -1) {
            return (d) => `${Number(d).toFixed(0)}ft`
        }
        if (type.indexOf('sprint_speed') > -1) {
            return (d) => `${Number(d).toFixed(1)}ft/s`
        }

        if (type === 'exchange_2b_3b_sba') {
            return (d) => `${Number(d).toFixed(2)}sec`
        }
        if (type === 'spin_rate') {
            return (d) => `${Number(d).toFixed(0)} RPM`
        }

        // Will use the default formatter
        return undefined
    }
    function getDomain(val, axis, data) {
        const domain = {}
        domain.min = d3.min(data, (d) => d[axis])

        domain.max = d3.max(data, (d) => d[axis])

        return domain
    }

    ScatterChart.prototype.parseData = function parseData(dataSet) {
        const x = []
        const y = []
        const n = dataSet.length
        let x_sum = 0
        let y_sum = 0
        let xy_sum = 0
        let xx_sum = 0
        let yy_sum = 0

        let term1 = 0
        let term2 = 0
        // create x and y values
        for (let i = 0; i < dataSet.length; i++) {
            const d = dataSet[i]
            x.push(Number(d.x))
            y.push(Number(d.y))

            x_sum += x[i]
            y_sum += y[i]
            xy_sum += x[i] * y[i]
            xx_sum += x[i] * x[i]
            yy_sum += y[i] * y[i]
        }

        // calculate mean x and y
        const x_mean = x_sum / n
        const y_mean = y_sum / n

        // calculate coefficients
        let xr = 0
        let yr = 0
        for (let i = 0; i < x.length; i++) {
            xr = x[i] - x_mean
            yr = y[i] - y_mean
            term1 += xr * yr
            term2 += xr * xr
        }
        const b1 = term1 / term2
        const b0 = y_mean - b1 * x_mean
        // perform regression

        const r2 =
            ((n * xy_sum - x_sum * y_sum) /
                Math.sqrt(
                    (n * xx_sum - x_sum * x_sum) * (n * yy_sum - y_sum * y_sum)
                )) **
            2

        const yhat = []
        // fit line using coeffs
        for (let i = 0; i < x.length; i++) {
            yhat.push(b0 + x[i] * b1)
        }

        const data = []
        for (let i = 0; i < y.length; i++) {
            data.push({
                yhat: yhat[i],
                y: y[i],
                x: x[i],
            })
        }
        data.forEach((d) => {
            d.x = +d.x
            d.y = +d.y
            d.yhat = +d.yhat
            d.r2 = +r2
        })

        return data
    }

    if (initCallback) {
        new ScatterChart(initOpts, (svg) => {
            initCallback(svg)
        })
    } else {
        new ScatterChart(initOpts)
    }
}

module.exports = { MakeChart }
